import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, money, errMsg, METAL_SWATCH } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useMeta } from "@/hooks/useMeta";
import { RingSizeHelp } from "@/components/RingSizeHelp";
import { PageHeader, Spinner, FinanceCue } from "@/components/common";
import { ShapeIcon } from "@/components/ShapeIcon";
import { imagesForMetal } from "@/pages/Product";

const STEPS = ["Choose Setting", "Choose Diamond", "Review"];
const SIZES = ["4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10"];

const Chip = ({ on, onClick, children, testId }: any) => (
  <button onClick={onClick} data-testid={testId} className={`px-4 py-2.5 text-sm border transition-colors ${on ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 bg-white hover:border-neutral-900"}`}>{children}</button>
);

function Stepper({ step, setStep, canNext }: any) {
  return (
    <ol className="grid grid-cols-3 border border-neutral-200 bg-white mb-12" data-testid="builder-stepper">
      {STEPS.map((s, i) => (
        <li key={s}>
          <button disabled={i > 0 && !canNext(i)} onClick={() => setStep(i)} data-testid={`builder-step-${i + 1}`}
            className={`w-full flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3 px-2 sm:px-4 md:px-8 py-3 sm:py-5 text-center sm:text-left transition-colors disabled:opacity-40 ${step === i ? "bg-[var(--c-muted)]" : "hover:bg-neutral-50"}`}>
            <span className={`h-8 w-8 flex-none rounded-full grid place-items-center text-xs border ${i < step ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-400"}`}>{i < step ? <Check size={14} /> : i + 1}</span>
            <span className="font-display text-[13px] leading-tight sm:text-lg">{s}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}

export default function RingBuilder() {
  const [params] = useSearchParams();
  const { add } = useCart();
  const [opts, setOpts] = useState<any>(null);
  const [step, setStep] = useState(0);
  const [setting, setSetting] = useState<any>(null);
  const [sel, setSel] = useState<any>({ shape: "Round", carat: 1.5, color: "F", clarity: "VS1", metal: "", size: "" });
  const [quote, setQuote] = useState<any>(null);
  useMeta("Design Your Own Engagement Ring | Somnio.Co", "Choose a setting, select your lab diamond by shape, carat, colour and clarity, and see live pricing.");

  useEffect(() => {
    api.get("/builder/options").then((r) => {
      setOpts(r.data);
      const pre = r.data.settings.find((s: any) => s.id === params.get("setting"));
      if (pre) pick(pre);
    });
  }, []);

  const pick = (s: any) => {
    setSetting(s);
    const shape = s.specs?.stone_shape && s.specs.stone_shape.length < 10 ? s.specs.stone_shape : "Round";
    setSel((x: any) => ({ ...x, shape, carat: s.specs?.stone_size || x.carat, metal: s.primary_metal || s.metals?.[0] }));
    setStep(1);
  };

  useEffect(() => {
    if (!setting) return;
    api.post("/builder/quote", { product_id: setting.id, metal: sel.metal, shape: sel.shape, carat: sel.carat, color: sel.color, clarity: sel.clarity })
      .then((r) => setQuote(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [setting, sel.metal, sel.shape, sel.carat, sel.color, sel.clarity]);

  const addToBag = () => {
    if (!sel.size) { toast.error("Please choose a ring size"); return; }
    add({ product_id: setting.id, name: `${setting.name} — Custom`, slug: setting.slug, image: imagesForMetal(setting, sel.metal)[0], metal: sel.metal, ring_size: sel.size,
      builder: { shape: sel.shape, carat: sel.carat, color: sel.color, clarity: sel.clarity }, unit_price: quote?.unit_price });
  };

  if (!opts) return <Spinner />;
  return (
    <div data-testid="ring-builder-page">
      <PageHeader eyebrow="Bespoke" title="Design Your Ring" intro="Three considered steps to a ring that is entirely yours. Prices update live as you choose." />
      <div className="max-w-[1400px] mx-auto px-5 md:px-12">
        <Stepper step={step} setStep={setStep} canNext={(i: number) => !!setting && (i < 2 || !!quote)} />

        {step === 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-10" data-testid="builder-settings-grid">
            {opts.settings.map((s: any) => (
              <button key={s.id} onClick={() => pick(s)} className={`group text-left ${setting?.id === s.id ? "ring-1 ring-neutral-900" : ""}`} data-testid={`builder-setting-${s.slug}`}>
                <div className="bg-white aspect-square overflow-hidden"><img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105" /></div>
                <p className="font-display text-lg mt-3 leading-snug group-hover:text-[var(--c-primary)]">{s.name}</p>
                <p className="text-sm text-neutral-500">from {money(s.price)}</p>
              </button>
            ))}
          </div>
        )}

        {step >= 1 && setting && (
          <div className="grid lg:grid-cols-[1fr_380px] gap-12">
            <div>
              {step === 1 ? (
                <div className="space-y-10" data-testid="builder-diamond-step">
                  <div><p className="eyebrow mb-4">Shape</p>
                    <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                      {opts.shapes.map((s: string) => (
                        <button key={s} onClick={() => setSel({ ...sel, shape: s })} data-testid={`builder-shape-${s.toLowerCase()}`}
                          className={`flex flex-col items-center gap-1.5 py-3 border text-[10px] transition-colors ${sel.shape === s ? "border-neutral-900 bg-white" : "border-neutral-200 hover:border-neutral-400"}`}>
                          <ShapeIcon shape={s} size={30} />{s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div><p className="eyebrow mb-4">Carat: <span className="normal-case tracking-normal" data-testid="builder-carat-value">{sel.carat} ct</span></p>
                    <input type="range" min={0} max={opts.carats.length - 1} step={1} value={Math.max(0, opts.carats.indexOf(sel.carat))}
                      onChange={(e) => setSel({ ...sel, carat: opts.carats[Number(e.target.value)] })} className="w-full accent-neutral-900" data-testid="builder-carat-slider" />
                    <div className="flex justify-between text-[10px] text-neutral-500 mt-1">{opts.carats.map((c: number) => <span key={c}>{c}</span>)}</div>
                  </div>
                  <div><p className="eyebrow mb-4">Colour</p><div className="flex flex-wrap gap-2">{opts.colors.map((c: string) => <Chip key={c} on={sel.color === c} onClick={() => setSel({ ...sel, color: c })} testId={`builder-color-${c}`}>{c}</Chip>)}</div></div>
                  <div><p className="eyebrow mb-4">Clarity</p><div className="flex flex-wrap gap-2">{opts.clarities.map((c: string) => <Chip key={c} on={sel.clarity === c} onClick={() => setSel({ ...sel, clarity: c })} testId={`builder-clarity-${c}`}>{c}</Chip>)}</div></div>
                  <div><p className="eyebrow mb-4">Metal</p><div className="flex flex-wrap gap-2">{setting.metals.map((m: string) => (
                    <Chip key={m} on={sel.metal === m} onClick={() => setSel({ ...sel, metal: m })} testId={`builder-metal-${m.replace(/\s+/g, "-").toLowerCase()}`}>
                      <span className="inline-block h-3 w-3 rounded-full mr-2 align-middle border border-neutral-300" style={{ background: METAL_SWATCH[m] }} />{m}</Chip>))}</div></div>
                  <button onClick={() => setStep(2)} className="btn-ink px-10 py-4 text-xs tracking-[0.25em] uppercase" data-testid="builder-continue-review">Continue to Review</button>
                </div>
              ) : (
                <div className="space-y-8" data-testid="builder-review-step">
                  <div className="bg-white aspect-[4/3] grid place-items-center"><img src={imagesForMetal(setting, sel.metal)[0]} alt={setting.name} className="max-h-full object-contain p-8" data-testid="builder-review-image" /></div>
                  <div><div className="flex justify-between items-center mb-4"><p className="eyebrow">Ring size</p><RingSizeHelp sizes={SIZES} value={sel.size} onPick={(s) => setSel({ ...sel, size: s })} testId="builder-size-help" /></div>
                    <div className="grid grid-cols-7 gap-2">{SIZES.map((s) => <Chip key={s} on={sel.size === s} onClick={() => setSel({ ...sel, size: s })} testId={`builder-size-${s}`}>{s}</Chip>)}</div>
                  </div>
                </div>
              )}
            </div>
            <aside className="lg:sticky lg:top-28 self-start border border-neutral-200 bg-white p-7 space-y-5" data-testid="builder-summary">
              <p className="eyebrow text-[var(--c-primary)]">Your Ring</p>
              <div className="flex gap-4"><img src={imagesForMetal(setting, sel.metal)[0]} alt="" className="h-20 w-20 object-contain bg-[var(--c-muted)]" data-testid="builder-summary-image" /><div><p className="font-display text-lg leading-tight">{setting.name}</p><button className="text-xs underline mt-1" onClick={() => setStep(0)} data-testid="builder-change-setting">Change setting</button></div></div>
              <dl className="text-sm space-y-2 border-t border-neutral-200 pt-4">
                <div className="flex justify-between"><dt className="text-neutral-500">Metal</dt><dd>{sel.metal}</dd></div>
                <div className="flex justify-between"><dt className="text-neutral-500">Centre stone</dt><dd>{sel.carat}ct {sel.shape}</dd></div>
                <div className="flex justify-between"><dt className="text-neutral-500">Colour / Clarity</dt><dd>{sel.color} / {sel.clarity}</dd></div>
                {sel.size && <div className="flex justify-between"><dt className="text-neutral-500">Size</dt><dd>{sel.size}</dd></div>}
              </dl>
              {quote && (
                <dl className="text-sm space-y-2 border-t border-neutral-200 pt-4">
                  <div className="flex justify-between"><dt>Setting</dt><dd>{money(quote.setting_price)}</dd></div>
                  <div className="flex justify-between"><dt>Centre stone adjustment</dt><dd>{quote.diamond_adjustment >= 0 ? "+" : "−"}{money(Math.abs(quote.diamond_adjustment))}</dd></div>
                  <div className="flex justify-between font-display text-2xl pt-2"><dt>Total</dt><dd data-testid="builder-total-price">{money(quote.unit_price)}</dd></div>
                </dl>
              )}
              {quote && <FinanceCue price={quote.unit_price} testId="builder-finance-cue" />}
              {step === 2 ? (
                <button onClick={addToBag} className="btn-ink w-full py-4 text-xs tracking-[0.25em] uppercase" data-testid="builder-add-to-bag">Add to Bag</button>
              ) : (
                <button onClick={() => setStep(2)} className="btn-line w-full py-4 text-xs tracking-[0.25em] uppercase" data-testid="builder-summary-review">Review Ring</button>
              )}
              <p className="text-[11px] text-neutral-500">Centre stone pricing is an estimate for a certified lab-grown diamond; our specialists will confirm stone selection after order.</p>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
